import React, { useState, useContext, useRef, useMemo } from 'react';
import { string, func } from 'prop-types';
import { Box, Flex } from 'rebass';
import { connect } from 'react-redux';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';
import { CSVLink } from 'react-csv';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';

import TableServerSide from '../../../components/tableServerSide/TableServerSide';
import { ActionItemList } from '../../common/ActionItemList';
import { showToast } from '../../../components/toast/actions';

import { generateValidationReport, fetchAllCatalogs, deleteAllCatalogs, deleteCatalog } from '../../catalogs/actions';

import { getLinkedCatalogsColumns } from './linkedCatalogsColumns';

import { formatCatalogForCSV } from '../../util';
import { TranslatedCopy } from '../../../intl';

import useModal from '../../../hooks/useModal';

const LinkedCatalogs = ({ catalogTypeId, generateValidationReport, showToast }) => {
  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();
  const [allCatalogs, setAllCatalogs] = useState([]);

  const csvLinkRef = useRef(null);

  const {
    tableInstance: { selectedRowIds = {}, isAllRowsSelected, selectedFlatRows },
    totalResources,
    refreshTableData,
    handleClearSortAndFilters,
    sortQueryParams,
    filterQueryParams
  } = useContext(TableServerSideContext);

  const selectedCatalogIds = selectedFlatRows?.reduce((acc, row) => ({ ...acc, [row?.original?.id]: true }), {}) || [];

  const clickCSVLink = async () => setTimeout(() => csvLinkRef?.current?.link.click(), 1000);

  const handleFetchAllCatalogs = async idsOnly => {
    const fetchedCatalogs = await fetchAllCatalogs({ totalResources, sortQueryParams, filterQueryParams });

    if (idsOnly) {
      return fetchedCatalogs.map(catalog => catalog.id);
    }

    return setAllCatalogs(fetchedCatalogs);
  };

  const actionItems = {
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                if (isAllRowsSelected) {
                  toggleIsLoading(true, 'Downloading CSV...');
                  await handleFetchAllCatalogs(false);
                  toggleIsLoading(false);
                }

                clickCSVLink();

                closeActionItemModal();
                showToast({ message: 'Catalog/s downloaded', type: 'success' });
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected catalog/s?',
        title: 'Confirm Download'
      }
    },
    'Validation Report': {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Downloading Validation Report...');

                const catalogIds = isAllRowsSelected ? await handleFetchAllCatalogs(true) : selectedCatalogIds;
                await generateValidationReport(catalogIds);

                toggleIsLoading(false);
                closeActionItemModal();
              }
          }
        ],
        content: () => 'Would you like to download the Validation Report?',
        title: 'Validation Report'
      }
    },
    '|': { action: () => {} },
    Delete: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Delete',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Deleting catalog...');

                try {
                  if (isAllRowsSelected) {
                    await deleteAllCatalogs(filterQueryParams);
                  } else {
                    const ids = Object.keys(selectedCatalogIds);

                    await Promise.all(ids.map(id => deleteCatalog(id)));
                  }

                  refreshTableData();

                  showToast(
                    {
                      type: 'success',
                      message: 'Catalogs deleted'
                    },
                    3000
                  );
                } catch (error) {
                  showToast({
                    message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
                    type: 'error',
                    error
                  });
                }

                refreshTableData();

                toggleIsLoading(false);
                closeActionItemModal();
                showToast({ message: 'Catalogs deleted', type: 'success' }, 5500);
              }
          }
        ],
        content: () => 'Do you want to proceed with deleting the selected catalog/s?',
        title: 'Confirm Deletion'
      }
    }
  };

  const memoizedColumns = useMemo(() => getLinkedCatalogsColumns(), []);

  const csvLinkData = (isAllRowsSelected ? allCatalogs : selectedFlatRows?.map(row => row.original))?.map(
    ({ id, name, hasDraft, ownerId, accountIds, ...catalog }) => formatCatalogForCSV(catalog, id, name)
  );

  return (
    <Box pt='15px'>
      <Flex alignItems='end'>
        <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
          {`${totalResources}`} results
        </TextStyled>

        <ButtonStyled
          appearance='text'
          background='light'
          ariaLabel='Clear All Filters'
          type='button'
          onClick={handleClearSortAndFilters}
          style={{ marginRight: '10px' }}
        >
          Clear All Filters
        </ButtonStyled>

        <ActionItemList
          selected={selectedRowIds}
          actionItems={actionItems}
          isModalOpen={isActionItemModalOpen}
          openModal={openActionItemModal}
          closeModal={closeActionItemModal}
        />

        <CSVLink data={csvLinkData || []} filename='LinkedCatalogs.csv' ref={csvLinkRef} target='_blank' />
      </Flex>

      <TableServerSide
        columns={memoizedColumns}
        url='v1/catalogs'
        extraParams={`&filter=catalogTypeId(${catalogTypeId})`}
        includeRowSelect
        fullHeight
      />
    </Box>
  );
};

LinkedCatalogs.propTypes = {
  catalogTypeId: string.isRequired,
  generateValidationReport: func.isRequired,
  showToast: func.isRequired
};

export default connect(null, { generateValidationReport, showToast })(LinkedCatalogs);
